/**
 * Defines the core, isomorphic building blocks of the Firebolt data layer.
 * Consumes `TemplatePath` to define type-safe collection paths.
 * The client module implements logic based on these shared definitions.
 * The admin module implements logic based on these shared definitions.
 * @see {@link https://zod.dev} - Zod is used extensively for schema definition and validation.
 *
 * This file is the heart of the `@firebolt` library. It establishes a "single source of truth" for data model-specs through the `ModelSpec` class.
 * A `ModelSpec` (created via `defineModelSpec`) encapsulates everything needed to understand a Firestore collection:
 * 1.  **Path:** A type-safe `TemplatePath` that defines the collection's location and dynamic segments.
 * 2.  **Shape:** A Zod schema (`z.object({...})`) that defines the structure of the documents within the collection.
 * 3.  **Constraints:** An array of default query constraints (e.g., `orderBy`) that should always be applied.
 *
 * By defining model-specs this way, we create a portable, environment-agnostic specification that can be used by both the client-side
 * and server-side Firestore SDKs to ensure they are operating on the exact same data structures and paths. This eliminates a
 * massive class of potential bugs and inconsistencies.
 *
 * The file also defines isomorphic representations of Firestore query constraints (`WhereSpec`, `OrderBySpec`, etc.), allowing
 * queries to be defined in a shared, abstract way.
 */

// Type definition for WhereFilterOp (environment-agnostic)
import type { PathInput, PathSegment, ZodDocId } from './TemplatePath.js';
import type { ZodFirestampNow } from './zod-types.js';

import { z } from 'zod';

import { zodParse } from '@elara/core/zod-utils';

import { TemplatePath } from './TemplatePath.js';
import { zFirestampNow } from './zod-types.js';

export type WhereFilterOp =
  | '<'
  | '<='
  | '=='
  | '!='
  | '>='
  | '>'
  | 'array-contains'
  | 'in'
  | 'not-in'
  | 'array-contains-any';

// Re-exporting Zod to ensure a single, consistent version is used across the project.
// This can prevent subtle bugs if different parts of the codebase accidentally import different Zod versions.
export type * from 'zod';

/**
 * A marker interface for a Zod raw shape definition (`z.ZodRawShape`).
 */
export type Shape = z.ZodRawShape;

/**
 * A utility type that takes a Zod shape and replaces any `ZodDocId` (branded string)
 * types with a plain `z.ZodString`. This is used internally to create the final Zod schema,
 * as the branding is only for path type-safety, not for the document data itself.
 */
export type UnbrandIds<S extends Shape> = { [K in keyof S]: S[K] extends ZodDocId<any> ? z.ZodString : S[K] };

/**
 * A marker interface for a Zod object schema (`z.ZodObject`).
 */
export interface Schema extends z.ZodObject<Shape> {}

// --- Firestore Data Types ---

export type FirestoreValue =
  | string
  | number
  | boolean
  | null
  | FirestoreTimestamp
  | FirestoreValue[]
  | { [key: string]: FirestoreValue };

export interface FirestoreTimestamp {
  seconds: number;
  nanoseconds: number;
  toDate(): Date;
  toMillis(): number;
}

/**
 * Defines the standard timestamp fields automatically added to all model-specs.
 */
export interface Timestamps {
  createdAt: Date;
  updatedAt: Date;
}

/**
 * A utility type representing any `ModelSpec` instance.
 */
export type AnyModelSpec = ModelSpec<any, any>;

/**
 * @class ModelSpec
 * @classdesc The core class for defining a Firestore collection's specification.
 * It bundles the path, schema, and default constraints into a single, portable object.
 * @template S - The Zod shape of the documents in the collection.
 * @template Path - The `TemplatePath` for the collection.
 */
export class ModelSpec<S extends Shape, Path extends TemplatePath> {
  /**
   * @constructor
   * @param path - The `TemplatePath` instance for the collection.
   * @param shape - The Zod shape for the documents (NOT ZodObject yet) see {this.schema}.
   * @param constraints - Default query constraints.
   */
  constructor(
    readonly path: Path,
    readonly shape: S,
    readonly constraints: ConstraintSpec[],
    readonly schema = z.object(shape).describe(`ModelSpecSchema[${path.toPath({})}]`),
  ) {}

  /**
   * @property {type} InputType - The TypeScript type for data used to create a document (before Zod parsing and defaults).
   */
  InputType!: z.input<typeof this.schema>;
  /**
   * @property {type} DataType - The TypeScript type for data read from a document (after Zod parsing and defaults).
   */
  DataType!: z.output<typeof this.schema>;

  /**
   * @get name
   * A computed property that derives a user-friendly name for the spec from its path.
   * @returns The name of the collection (e.g., "chats").
   */
  get name(): string {
    const [slug, _id] = this.path.lastPair;
    let name = slug;
    if (name.startsWith('/')) {
      name = name.slice(1);
    }
    if (name.endsWith('/')) {
      name = name.slice(0, -1);
    }
    return name;
  }

  /**
   * Parses and validates raw data against the spec's schema.
   * @param data - The raw input or output data.
   * @returns The parsed and validated data.
   */
  read(data: any): this['DataType'] {
    try {
      return zodParse(this.schema, data, {
        message: `Failed to validate incoming data for ${this.name} model`,
      }) as this['DataType'];
    } catch (error) {
      console.error(`[ModelSpec] Error parsing data for ${this.name}`, { error, data });
      throw new Error(
        `[ModelSpec] Error parsing data for ${this.name}: ${error instanceof Error ? error.message : String(error)}`,
        { cause: error },
      );
    }
  }

  /**
   * Returns a string representation of the `ModelSpec`.
   * @returns
   */
  toString(): string {
    return `ModelSpec(${this.name}: ${this.path.toPath({})})`;
  }
}

export interface ZodTimestamps {
  createdAt: ZodFirestampNow;
  updatedAt: ZodFirestampNow;
}

/**
 * The primary factory function for creating a `ModelSpec`.
 * It automatically adds `createdAt` and `updatedAt` timestamp fields to the shape.
 *
 * @param options - The specification options.
 * @param options.shape - The Zod shape of the document.
 * @param options.path - The `TemplatePath` of the collection.
 * @param [options.constraints=[]] - Default query constraints.
 * @returns A new `ModelSpec` instance.
 */
export function defineModelSpec<S extends Shape, Path extends TemplatePath>({
  shape,
  path,
  constraints = [],
}: {
  shape: S;
  path: Path;
  constraints?: ConstraintSpec[];
}): ModelSpec<S & ZodTimestamps, Path> {
  return new ModelSpec(
    path,
    { ...shape, createdAt: zFirestampNow(), updatedAt: zFirestampNow() } as const,
    constraints,
  );
}

/**
 * Defines the common interface for a reactive model instance on the client or a plain data object on the server.
 * @property id - The document ID.
 * @property path - The full Firestore path to the document.
 * @property [_hydrate] - (Client-only) A method to surgically update the instance with new data.
 * @property [toFirestore] - (Client-only) A method to serialize the instance back to a plain object for writing.
 */
export interface AbstractBaseInstance<DataType> {
  readonly id: string;
  readonly path: string;
  readonly ref: { id: string; path: string };
  _hydrate?(data: Partial<DataType>): void;
  toFirestore(): DataType;
}

/**
 * A utility type to extract the required context object for rendering a `ModelSpec`'s path.
 */
export type PathProps<Spec> = Spec extends { path: TemplatePath<infer Segments> }
  ? PathInput<Segments>
  : PathInput<string[]>;

export type DocPathProps<Spec> = PathProps<Spec> & { id: string };
export type CollectionPathProps<Spec> = PathProps<Spec>;

// --- Isomorphic Query Constraint Definitions ---

/**
 * A union type representing all possible isomorphic query constraints.
 */
export type ConstraintSpec =
  | WhereSpec
  | OrderBySpec
  | StartAtSpec
  | StartAfterSpec
  | EndBeforeSpec
  | EndAtSpec
  | LimitSpec
  | LimitToLastSpec;

export interface WhereSpec {
  type: 'where';
  field: string;
  operator: WhereFilterOp;
  value: any;
}

export function where(field: string, operator: WhereFilterOp, value: any): WhereSpec {
  return { type: 'where', field, operator, value };
}

export interface OrderBySpec {
  type: 'orderBy';
  field: string;
  direction: 'asc' | 'desc';
}

export function orderBy(field: string, direction: 'asc' | 'desc' = 'asc'): OrderBySpec {
  return { type: 'orderBy', field, direction };
}

export interface LimitSpec {
  type: 'limit';
  limit: number;
}

export function limit(limit: number): LimitSpec {
  return { type: 'limit', limit };
}

export interface LimitToLastSpec {
  type: 'limitToLast';
  limitToLast: number;
}

export function limitToLast(limitToLast: number): LimitToLastSpec {
  return { type: 'limitToLast', limitToLast };
}

export interface StartAtSpec {
  type: 'startAt';
  values: any[];
}

export function startAt<S extends Shape>(...values: Array<z.input<S[keyof S]>>): StartAtSpec {
  return { type: 'startAt', values };
}

export interface StartAfterSpec {
  type: 'startAfter';
  values: any[];
}

export function startAfter<S extends Shape>(...values: Array<z.input<S[keyof S]>>): StartAfterSpec {
  return { type: 'startAfter', values };
}

export interface EndBeforeSpec {
  type: 'endBefore';
  values: any[];
}

export function endBefore(...values: any[]): EndBeforeSpec {
  return { type: 'endBefore', values };
}

export interface EndAtSpec {
  type: 'endAt';
  values: Array<string | number | boolean | Date | FirestoreTimestamp>;
}

export function endAt(...values: EndAtSpec['values']): EndAtSpec {
  return { type: 'endAt', values };
}

export type FieldPathSpec = string;

/**
 * The primary path builder for creating `TemplatePath` instances for Firestore collections.
 * It includes a validation rule to ensure that paths always have an even number of segments (collection/doc/collection/doc...).
 */
export const collectionPath = TemplatePath.createBuilder((segments: PathSegment[], staticSegments: string[]) => {
  //  verify that all segments come in pairs
  return (segments.length + staticSegments.length) % 2 === 0;
});

export type DataOf<Spec extends AnyModelSpec> = Spec['DataType'];

export type InputOf<Spec extends AnyModelSpec> = Spec['InputType'];
